import { Button, Combobox, Field, Input, Radio } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { CONTACT_NAME_LABELS } from '@/lib/contact-name';
import type { ContactGroup } from '@/lib/types';
import type {
  DraftContact,
  DraftContactErrors,
  DraftContactPatch,
} from '@/components/companies/company-contacts-draft';

export interface CompanyContactsDraftProps {
  rows: DraftContact[];
  errors: DraftContactErrors;
  /** The row marked primary, or `null` when nobody is. */
  primaryKey: string | null;
  groups: ContactGroup[];
  groupsLoading: boolean;
  /*
   * ⚠️ No `groupsFailed`. It fed a hint and a footnote, both removed under the
   * owner's stricter helper-text ruling (2026-09-05), and nothing else read it.
   * A failed groups read now renders as an empty picker — the group is optional
   * and clears on `null`, so nothing is lost but the explanation.
   */
  disabled: boolean;
  /** Shared by every primary radio, which is what makes the set exclusive. */
  radioName: string;
  onChange: (key: string, patch: DraftContactPatch) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
  onPrimaryChange: (key: string | null) => void;
}

/**
 * People to create alongside a company — a new one, or (since 2026-10-15) an
 * existing one being edited from the companies list.
 *
 * *SUPERSEDED 2026-10-15: "⚠️ CREATE ONLY. Editing a company's contacts belongs
 * on the detail page."* The edit form had no way to add a person at all, which
 * was reported as a bug. On edit these rows only ADD contacts; editing or
 * re-parenting an existing contact still belongs on the contact itself.
 *
 * ⚠️ **These rows are NOT part of the company payload.** `POST /companies` is
 * `.strict()` and a `contacts` key is a 422, so each row becomes its own
 * `POST /contacts` after the company returns an id. That is a real two-phase
 * write with no transaction across it, and the form that owns this component is
 * responsible for reporting the halves separately — see its create branch.
 *
 * Deliberately a handful of fields rather than a second `ContactFormModal`:
 * the three name fields follow the app-wide rule in `lib/contact-name.ts`
 * (First name, Last name, Full name — owner 2026-10-16), and somebody adding three
 * people while filing a new school is not filling in thirty columns for each.
 * Everything else is edited on the contact afterwards.
 */
export function CompanyContactsDraft({
  rows,
  errors,
  primaryKey,
  groups,
  groupsLoading,
  disabled,
  radioName,
  onChange,
  onAdd,
  onRemove,
  onPrimaryChange,
}: CompanyContactsDraftProps) {
  const groupOptions: ComboboxOption[] = groups.map((group) => ({
    value: String(group.id),
    label: group.name,
  }));

  return (
    <div className="grid gap-3">
      {rows.map((row, index) => {
        const rowErrors = errors[row.key] ?? {};
        const isPrimary = primaryKey === row.key;

        return (
          <fieldset key={row.key} className="rounded-md border border-border px-3 py-3">
            <legend className="px-1 text-xs font-semibold text-fg-secondary">
              Contact {index + 1}
            </legend>

            <div className="grid gap-3 sm:grid-cols-2">
              {/* Order and labels: `lib/contact-name.ts`, one rule app-wide. */}
              <Field
                label={CONTACT_NAME_LABELS.firstName}
                required
                error={rowErrors.firstName}
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={row.firstName}
                    maxLength={120}
                    autoComplete="off"
                    invalid={Boolean(rowErrors.firstName)}
                    disabled={disabled}
                    aria-describedby={describedBy}
                    onChange={(event) => onChange(row.key, { firstName: event.target.value })}
                  />
                )}
              </Field>

              <Field label={CONTACT_NAME_LABELS.lastName}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={row.lastName}
                    maxLength={120}
                    autoComplete="off"
                    disabled={disabled}
                    aria-describedby={describedBy}
                    onChange={(event) => onChange(row.key, { lastName: event.target.value })}
                  />
                )}
              </Field>

              <div className="sm:col-span-2">
                <Field label={CONTACT_NAME_LABELS.name} required error={rowErrors.name}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Input
                      id={id}
                      value={row.name}
                      maxLength={240}
                      autoComplete="off"
                      invalid={Boolean(rowErrors.name)}
                      disabled={disabled}
                      aria-describedby={describedBy}
                      onChange={(event) => onChange(row.key, { name: event.target.value })}
                    />
                  )}
                </Field>
              </div>

              <Field label="Email" error={rowErrors.email}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="email"
                    value={row.email}
                    maxLength={255}
                    autoComplete="off"
                    invalid={Boolean(rowErrors.email)}
                    disabled={disabled}
                    aria-describedby={describedBy}
                    onChange={(event) => onChange(row.key, { email: event.target.value })}
                  />
                )}
              </Field>

              {/* `phone` on the contact; the server checks length (32) only. */}
              <Field label="Phone">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="tel"
                    value={row.phone}
                    maxLength={32}
                    autoComplete="off"
                    disabled={disabled}
                    aria-describedby={describedBy}
                    onChange={(event) => onChange(row.key, { phone: event.target.value })}
                  />
                )}
              </Field>

              {/*
               * Options come from `GET /contact-groups`, never from a literal.
               * Finance and Operations are what is seeded today, not what the
               * table is limited to — an admin adding a third must appear here
               * with no deploy, the same way the permission grid survived three
               * changes to the permission count by rendering from its response.
               */}
              {/*
               * ⚠️ Both load-failure sentences are GONE — owner's stricter
               * helper-text ruling, 2026-09-05. The hint read "Contact groups
               * couldn't be loaded." and the footnote "Contact groups could not
               * be loaded." Neither is a server refusal.
               */}
              <Field label="Group" error={undefined}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={row.contactGroupId}
                    options={groupOptions}
                    placeholder={groupsLoading ? 'Loading groups…' : 'No group'}
                    emptyOptionLabel="No group"
                    searchPlaceholder="Search groups…"
                    loading={groupsLoading && groupOptions.length === 0}
                    disabled={disabled || groupsLoading}
                    aria-describedby={describedBy}
                    onChange={(next) => onChange(row.key, { contactGroupId: next })}
                  />
                )}
              </Field>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              {/*
               * Exclusivity is the shared `name`, which is the platform's own
               * mechanism — one tab stop for the set and arrow keys between the
               * rows, neither of which a hand-rolled version gets for free.
               *
               * The `onClick` is what makes the mark CLEARABLE: a radio cannot
               * be unchecked by clicking it, and no primary at all is a legal
               * outcome — most companies are created without one. Clicking the
               * checked row clears it (click fires, change does not, because
               * nothing changed); clicking an unchecked row sets it (click is a
               * no-op, then change fires).
               */}
              <Radio
                name={radioName}
                checked={isPrimary}
                disabled={disabled}
                label="Primary contact"
                onChange={() => onPrimaryChange(row.key)}
                onClick={() => {
                  if (isPrimary) onPrimaryChange(null);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(row.key)}
                disabled={disabled}
              >
                Remove
              </Button>
            </div>
          </fieldset>
        );
      })}

      <div>
        <Button variant="secondary" size="sm" onClick={onAdd} disabled={disabled}>
          Add contact
        </Button>
      </div>
    </div>
  );
}
